import { balance } from '@game/gamedata';

/**
 * Versão do comportamento do sim.
 * ALWAYS incremente quando o resultado de uma partida mudar de propósito.
 * O corpus de replays é indexado por esta versão: replay antigo continua
 * rodando contra a versão em que foi gravado.
 */
export const SIM_VERSION = 4;

export interface PlayerState {
  id: number;
  x: number;
  z: number;
  /** Velocidade do direcional. Morre com `player.friction` quando o dedo sai. */
  vx: number;
  vz: number;
  /**
   * Velocidade que o CAMPO deu, somada à do direcional na hora de andar.
   *
   * Campo separado de propósito: a velocidade do direcional é zerada em 0,2 s
   * pela fricção, e um impulso magnético que morre em 0,2 s NEVER atravessa vão
   * nenhum. Este freia por `magnet.impulseDrag`, que é outra ordem de grandeza.
   */
  ix: number;
  iz: number;
  /** Última direção intencional. É por ela que o objeto capturado é lançado. */
  facingX: number;
  facingZ: number;
  /** Corpo magnetizado agora; 0 = campo desligado. */
  magnetTargetId: number;
  /**
   * O botão do ímã está SEGURADO. Estado, e NEVER evento.
   *
   * Existe porque o `wantsMagnet` era lido do input DAQUELE tick, e tick sem
   * input novo significava "soltou". Cliente e servidor correm a 30 Hz em
   * relógios independentes, então tick vazio é NORMAL — e cada um deles largava
   * o campo, que o tick seguinte reengatava.
   *
   * MEDIDO no aparelho real, com servidor local a 30 Hz limpos: **20 trocas de
   * alvo em 40 segundos** com o dedo NUNCA saindo do botão — zero `pointerup` em
   * 45 s de gravação. Era o "aperto, seguro, e quando ando ele solta e pega".
   *
   * Botão segurado é ESTADO: quem o mudou foi o último input que chegou, e a
   * ausência de input NEVER é a informação "soltei". ALWAYS trate assim todo
   * controle mantido — perda de pacote NEVER pode virar comando.
   */
  magnetHeld: boolean;
  hp: number;
  /** Último seq de input aplicado. Base do ack de reconciliação. */
  lastSeq: number;
}

/**
 * `anchor` está preso ao cenário e os outros dois não. É a `fixação` do PULSO, e é
 * ela que decide QUEM se move: contra o preso cede o robô, contra o solto cede o
 * objeto (`gameplay.target_states`).
 *
 * `light`, `heavy` e `core` diferem só em DADO — massa e `capturable` em
 * `gamedata` —, e é de propósito: o tipo aqui existe para o fio e para o hash
 * saberem nomear o corpo, e NEVER para a física ganhar um caminho por tipo.
 *
 * `core` é o Núcleo Vivo, e o que ele tem de especial NEVER está na física: está
 * em quem OLHA para ele. A sala pergunta se um corpo desse tipo entrou na saída
 * (`objective.ts`) e encerra a partida — do mesmo jeito que o fim por tempo, que
 * também NEVER mora no `step`. Por isso acrescentar este tipo NEVER mexeu em
 * `SIM_VERSION`: nenhum tick passa a se comportar diferente, e todo fixture do
 * corpus continua reproduzindo o que gravou.
 */
export type BodyKind = 'anchor' | 'light' | 'heavy' | 'core';

/**
 * Código estável do tipo de corpo. Usado pelo hash de estado E pelo fio.
 *
 * Hashear a STRING seria caro e frágil; hashear o índice de um array seria pior,
 * porque reordenar `bodies` em `gamedata` mudaria o hash de toda partida em
 * silêncio. Aqui a amarra é explícita: acrescentar tipo novo é acrescentar número
 * novo — **NEVER renumerar**. Renumerar invalida o corpus de replay inteiro e
 * troca o tipo de corpo debaixo de todo cliente conectado ao mesmo tempo.
 *
 * `Record<BodyKind, number>` de propósito: tipo novo sem código é erro de
 * compilação, e NEVER um `undefined` que vira 0 no fio.
 */
const KIND_CODE: Record<BodyKind, number> = { anchor: 1, light: 2, heavy: 3, core: 4 };

/**
 * Os tipos em ordem estável. Existe como ARRAY, e NEVER como `Object.keys`, pela
 * mesma razão que a regra `sim-determinism` proíbe: ordem de iteração de objeto
 * NEVER é contrato, e aqui ela decidiria o conteúdo de uma tabela do fio.
 */
const KIND_ORDER = ['anchor', 'light', 'heavy', 'core'] as const satisfies readonly BodyKind[];

/** Erro de compilação se um `BodyKind` novo ficar fora de `KIND_ORDER`. */
type AssertNever<T extends never> = T;
export type _NoKindMissingFromOrder = AssertNever<
  Exclude<BodyKind, (typeof KIND_ORDER)[number]>
>;

const KIND_BY_CODE = new Map<number, BodyKind>(KIND_ORDER.map((kind) => [KIND_CODE[kind], kind]));

export function bodyKindCode(kind: BodyKind): number {
  return KIND_CODE[kind];
}

/**
 * Volta do código para o tipo. Devolve `undefined` para código desconhecido, e
 * isso NEVER é defensividade vazia: é exatamente o que um cliente antigo recebe
 * quando o servidor passa a mandar um tipo de corpo que ele não conhece.
 */
export function bodyKindFromCode(code: number): BodyKind | undefined {
  return KIND_BY_CODE.get(code);
}

export interface BodyState {
  id: number;
  kind: BodyKind;
  x: number;
  z: number;
  vx: number;
  vz: number;
  /** Jogador que capturou o corpo; 0 = ninguém o carrega. */
  heldBy: number;
}

/**
 * Uma zona de PERIGO: círculo no chão que empurra para fora quem estiver dentro.
 *
 * Vive no estado, e NEVER só no `gamedata`, porque ela MUDA A FÍSICA: o replay
 * precisa recriar o mundo exato que rodou, e uma sala gravada sem a zona
 * reproduziria uma partida em que ninguém foi empurrado. É a diferença exata
 * entre isto e a SAÍDA (`objective.ts`), que NEVER toca na simulação e por isso
 * NEVER entra aqui.
 */
export interface HazardZone {
  readonly x: number;
  readonly z: number;
  readonly radius: number;
  /** Aceleração radial para fora, u/s². */
  readonly push: number;
  /** Teto da velocidade que o empurrão dá, u/s. */
  readonly maxSpeed: number;
}

/** Descrição de sala: só o que o conteúdo escolhe. O resto o sim deriva. */
export interface RoomLayout {
  readonly bodies: readonly { readonly kind: BodyKind; readonly x: number; readonly z: number }[];
  /**
   * OPCIONAL de propósito: todo fixture de replay gravado antes do perigo NEVER
   * tem este campo, e sala sem zona é exatamente a sala que eles gravaram. É isso
   * que mantém o corpus válido sem `SIM_VERSION` novo.
   */
  readonly hazards?: readonly HazardZone[];
}

export interface GameState {
  tick: number;
  seed: number;
  /** Estado do RNG. Entra no hash. */
  rngState: number;
  /**
   * ALWAYS ordenado por id crescente.
   * Array ordenado em vez de Map de propósito: iteração de Map depende de
   * ordem de inserção, e o sim NEVER pode depender disso (REV-06).
   */
  players: PlayerState[];
  /** Mesma regra dos jogadores: ordenado por id, e NEVER Map. */
  bodies: BodyState[];
  /**
   * As zonas de perigo, na ordem em que o conteúdo as descreveu. Estáticas: nada
   * no jogo move, cria ou apaga uma zona durante a partida, e é por isso que elas
   * NEVER entram no hash de estado — hashear o que NEVER muda é custo por tick
   * para provar o que o layout gravado já prova.
   */
  hazards: HazardZone[];
}

export function createState(seed: number): GameState {
  return { tick: 0, seed: seed >>> 0, rngState: seed >>> 0, players: [], bodies: [], hazards: [] };
}

/**
 * Põe os corpos da sala no estado. Chamada EXPLÍCITA, e NEVER embutida no
 * `createState`: sala sem corpo é o que o servidor roda hoje, e o sim NEVER
 * decide que toda partida tem ímã.
 *
 * Substitui o que houver: carregar sala duas vezes NEVER empilha mundo.
 */
export function loadRoom(state: GameState, layout: RoomLayout): void {
  state.bodies.length = 0;
  state.hazards.length = 0;
  for (const zone of layout.hazards ?? []) state.hazards.push(zone);
  for (const [index, body] of layout.bodies.entries()) {
    state.bodies.push({
      id: index + 1,
      kind: body.kind,
      x: body.x,
      z: body.z,
      vx: 0,
      vz: 0,
      heldBy: 0,
    });
  }
}

export function findBody(state: GameState, id: number): BodyState | undefined {
  for (const body of state.bodies) {
    if (body.id === id) return body;
  }
  return undefined;
}

export function findPlayer(state: GameState, id: number): PlayerState | undefined {
  // Máximo de balance.room.maxPlayers jogadores: busca linear é mais rápida
  // que binária nesse tamanho, e trivialmente determinística.
  for (const player of state.players) {
    if (player.id === id) return player;
  }
  return undefined;
}

/**
 * Posição de spawn derivada do id, distribuída no círculo.
 *
 * Usa Math.cos/Math.sin de propósito: o determinismo exigido é o de REPLAY
 * (mesma máquina, mesmo build), não bit-exato entre máquinas — ver REV-02.
 * Servidor autoritativo com predição tolera divergência de última casa.
 */
function spawnPosition(id: number, total: number): { x: number; z: number } {
  const spawnRadius = balance.arena.radius * 0.6;
  const angle = (Math.PI * 2 * ((id - 1) % Math.max(total, 1))) / Math.max(total, 1);
  return { x: Math.cos(angle) * spawnRadius, z: Math.sin(angle) * spawnRadius };
}

export function addPlayer(state: GameState, id: number): PlayerState {
  const existing = findPlayer(state, id);
  if (existing !== undefined) return existing;

  const spawn = spawnPosition(id, balance.room.maxPlayers);
  const player: PlayerState = {
    id,
    x: spawn.x,
    z: spawn.z,
    vx: 0,
    vz: 0,
    ix: 0,
    iz: 0,
    // Olhando para longe da câmera. Sem direção inicial, o primeiro lançamento
    // antes de tocar no direcional NEVER teria para onde ir.
    facingX: 0,
    facingZ: -1,
    magnetTargetId: 0,
    magnetHeld: false,
    hp: balance.player.startHp,
    lastSeq: 0,
  };
  state.players.push(player);
  state.players.sort((a, b) => a.id - b.id);
  return player;
}

export function removePlayer(state: GameState, id: number): void {
  const index = state.players.findIndex((player) => player.id === id);
  if (index >= 0) state.players.splice(index, 1);
}

export function cloneState(state: GameState): GameState {
  return {
    tick: state.tick,
    seed: state.seed,
    rngState: state.rngState,
    players: state.players.map((player) => ({ ...player })),
    bodies: state.bodies.map((body) => ({ ...body })),
    // A zona é IMUTÁVEL e estática: o array é novo (o clone NEVER pode ficar
    // preso ao do original), mas as zonas são as mesmas referências. Copiar
    // objeto que ninguém escreve seria lixo por rollback, e rollback acontece
    // a cada snapshot no cliente.
    hazards: [...state.hazards],
  };
}

/** Copia `from` para dentro de `into`, sem alocar novo estado. Usado na reconciliação. */
export function copyStateInto(into: GameState, from: GameState): void {
  into.tick = from.tick;
  into.seed = from.seed;
  into.rngState = from.rngState;
  into.players.length = 0;
  for (const player of from.players) into.players.push({ ...player });
  into.bodies.length = 0;
  for (const body of from.bodies) into.bodies.push({ ...body });
  into.hazards.length = 0;
  for (const zone of from.hazards) into.hazards.push(zone);
}
